import { Icon } from "@/components/icon/Icon";
import { PLANS, formatCount } from "@/lib/plans";

function Yes({ label }: { label: string }) {
  return (
    <span className="inline-flex items-center gap-1 text-label-normal">
      <Icon name="check" size={18} style={{ color: "var(--primary-normal)" }} />
      <span className="sr-only">{label} 가능</span>
    </span>
  );
}

function No({ label }: { label: string }) {
  return (
    <span className="inline-flex items-center text-label-neutral">
      <Icon name="minus" size={18} />
      <span className="sr-only">{label} 불가</span>
    </span>
  );
}

/** Plan table from the design doc. Prices are not set yet, so none are shown. */
export function Plans() {
  return (
    <section id="plans" aria-labelledby="plans-title" className="scroll-mt-20">
      <div className="mx-auto max-w-layout px-5 py-20">
        <h2 id="plans-title" className="wds-title-2 text-label-strong break-keep">
          요금제
        </h2>
        <p className="mt-4 max-w-xl text-body-1 font-regular text-label-neutral break-keep">
          초안을 만든 횟수로만 세요. 올라간 후기 수로는 세지 않아요. 가격은 정식 출시 때 알려
          드리고, 지금은 무료 요금제로 시작할 수 있어요.
        </p>

        <ul className="mt-8 flex flex-col gap-4 md:hidden">
          {PLANS.map((p) => (
            <li
              key={p.id}
              className="rounded-lg p-5 shadow-[inset_0_0_0_1px_var(--line-normal-neutral)]"
            >
              <h3 className="wds-headline-1 text-label-strong">{p.name}</h3>
              <dl className="mt-3 grid grid-cols-[auto_1fr] gap-x-6 gap-y-2 text-body-2">
                <dt className="text-label-neutral">후기 페이지</dt>
                <dd className="tabular-nums text-label-normal">
                  {p.pages === null ? "무제한" : `${p.pages}개`}
                </dd>
                <dt className="text-label-neutral">월 초안 생성</dt>
                <dd className="tabular-nums text-label-normal">
                  {p.monthly_drafts === null ? "무제한" : `${formatCount(p.monthly_drafts)}회`}
                </dd>
                <dt className="text-label-neutral">칩 문구</dt>
                <dd className="text-label-normal">
                  {p.chip_edit ? "고칠 수 있어요" : "켜고 끄기만"}
                </dd>
                <dt className="text-label-neutral">대표색</dt>
                <dd className="text-label-normal">{p.accent ? "바꿀 수 있어요" : "기본 파랑"}</dd>
                <dt className="text-label-neutral">텔뷰 표시</dt>
                <dd className="text-label-normal">
                  {p.hide_badge ? "숨길 수 있어요" : "표시돼요"}
                </dd>
              </dl>
            </li>
          ))}
        </ul>

        <div className="tv-scroll-x mt-10 hidden overflow-x-auto md:block">
          <table className="w-full min-w-[640px] border-collapse text-left">
            <caption className="sr-only">요금제별 한도와 기능</caption>
            <thead>
              <tr className="shadow-[inset_0_-1px_0_var(--line-normal-normal)]">
                <th
                  scope="col"
                  className="w-[28%] py-4 pr-4 text-label-1 font-semibold text-label-neutral"
                >
                  <span className="sr-only">항목</span>
                </th>
                {PLANS.map((p) => (
                  <th key={p.id} scope="col" className="px-4 py-4 wds-headline-1 text-label-strong">
                    {p.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="text-body-1 text-label-normal">
              <Row label="후기 페이지">
                {PLANS.map((p) => (
                  <td key={p.id} className="px-4 py-4 tabular-nums">
                    {p.pages === null ? "무제한" : `${p.pages}개`}
                  </td>
                ))}
              </Row>
              <Row label="월 초안 생성">
                {PLANS.map((p) => (
                  <td key={p.id} className="px-4 py-4 tabular-nums">
                    {p.monthly_drafts === null ? "무제한" : `${formatCount(p.monthly_drafts)}회`}
                  </td>
                ))}
              </Row>
              <Row label="칩 문구 고치기">
                {PLANS.map((p) => (
                  <td key={p.id} className="px-4 py-4">
                    {p.chip_edit ? (
                      <Yes label="칩 문구 고치기" />
                    ) : (
                      <span className="text-label-neutral">켜고 끄기만</span>
                    )}
                  </td>
                ))}
              </Row>
              <Row label="대표색 바꾸기">
                {PLANS.map((p) => (
                  <td key={p.id} className="px-4 py-4">
                    {p.accent ? <Yes label="대표색 바꾸기" /> : <No label="대표색 바꾸기" />}
                  </td>
                ))}
              </Row>
              <Row label="텔뷰 표시" last>
                {PLANS.map((p) => (
                  <td key={p.id} className="px-4 py-4 text-label-neutral">
                    {p.hide_badge ? "숨길 수 있어요" : "표시돼요"}
                  </td>
                ))}
              </Row>
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

function Row({
  label,
  children,
  last = false,
}: { label: string; children: React.ReactNode; last?: boolean }) {
  return (
    <tr className={last ? "" : "shadow-[inset_0_-1px_0_var(--line-normal-alternative)]"}>
      <th scope="row" className="py-4 pr-4 text-body-2 font-semibold text-label-neutral">
        {label}
      </th>
      {children}
    </tr>
  );
}
